import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { Heart, ArrowLeft } from "lucide-react";

export default function Privacy() {
    return (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-16 leading-relaxed text-[#1F2624]">
            <SeoHead
                title="Memory Lane Care — Privacy Policy"
                description="How Memory Lane Care collects, uses, and protects information from caregivers and the people they support. Plain-English data handling, no data sales, no ad tracking."
                canonicalPath="/privacy"
            />
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6">
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
            </Link>
            <header className="border-b border-[#E2E8F0] pb-6 mb-8">
                <div className="flex items-center gap-2.5 text-teal-dark mb-3">
                    <Heart size={22} strokeWidth={1.6} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">Privacy Policy</h1>
                <p className="mt-3 text-sm text-[#4C5551]">Last updated: February 2026</p>
            </header>

            {/* Hero callout — friendly, beta-framed transparency. We want
                caregivers to know what Memory Lane is (and isn't) without
                feeling like they're reading a legal warning. */}
            <aside
                className="rounded-2xl border border-teal-soft bg-teal-soft/30 p-5 mb-8"
                data-testid="privacy-hipaa-callout"
                role="note"
                aria-label="Beta disclosure"
            >
                <p className="text-sm font-semibold text-teal-dark uppercase tracking-wider">A friendly heads-up</p>
                <p className="text-[15px] text-[#1F2624] mt-1.5 leading-relaxed">
                    Memory Lane is currently in <strong>beta</strong> and is <strong>not yet HIPAA-compliant</strong>. It&apos;s a tool to help you learn, organize, and prepare — not a substitute for clinical advice. We treat your data with care and you can ask us to export or delete it anytime; for records that legally need to be Protected Health Information (PHI), please use your loved one&apos;s clinic or hospice patient portal.
                </p>
            </aside>

            <Section title="In plain English">
                <p>
                    Memory Lane is built for caregivers in a vulnerable moment. We treat your data the way we&apos;d want our own family&apos;s data treated. We collect only what we need to make Memory Lane work for you, we don&apos;t sell it, we don&apos;t share it with advertisers, and we don&apos;t use it to train outside AI models.
                </p>
                <p>
                    You can ask us to show, export, or delete your data at any time by emailing <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>. We&apos;ll respond within 30 days.
                </p>
                <p>
                    Want the cookie-by-cookie breakdown? See our <Link to="/cookies" className="text-teal-dark underline">Cookie Policy</Link>.
                </p>
            </Section>

            <Section title="Memory Lane is not HIPAA-covered">
                <p>
                    <strong>Memory Lane is not a HIPAA-covered entity, and what you enter here is not Protected Health Information (PHI) in the legal sense.</strong> HIPAA only applies to healthcare providers, health plans, and the &quot;business associates&quot; they contract with. Memory Lane is a self-service caregiver tool — we are not your loved one&apos;s clinic, hospice, or insurance company, and we are not under contract with any of them.
                </p>
                <p>
                    Your data here is protected by this Privacy Policy and the U.S. privacy laws that apply to ordinary consumer services (including CCPA-style rights in some states), but it is <em>not</em> protected by HIPAA. If you want a record that is legally treated as PHI, keep it in your loved one&apos;s hospital or clinic patient portal — not in Memory Lane.
                </p>
                <p>
                    Please <strong>do not</strong> enter your loved one&apos;s social security number, full insurance ID, or full credit card number into Memory Lane. We don&apos;t need them and we don&apos;t want to store them.
                </p>
            </Section>

            <Section title="What we collect">
                <ul>
                    <li><strong>Account info</strong> — your name, email, password (stored hashed with bcrypt), the country you&apos;re caregiving from, the relationship you described at signup, and your &quot;biggest challenge&quot; note.</li>
                    <li><strong>Intake answers</strong> — relationship, living situation, FAST stage, top concerns, safety priorities, family support level, burnout score, hours per week.</li>
                    <li><strong>Caregiving content you create</strong> — patient profile (medications, doctors, allergies, etc.), behavior logs (incidents, triggers, what helped), appointment notes, drafted messages, Lane chat history, GUIDE screener answers, Continuity Passport data, document uploads.</li>
                    <li><strong>Usage signals</strong> — login timestamps, IP, user agent, which sections you visited, what you searched for, when you completed the tour, when you marked items done on your roadmap.</li>
                    <li><strong>Feedback</strong> — anything you submit through the Feedback page.</li>
                </ul>
            </Section>

            <Section title="What we don&apos;t collect">
                <ul>
                    <li>We do <strong>not</strong> ask for your loved one&apos;s social security number, full date of birth (we only ask for year), or insurance ID. If you choose to add those to the patient profile, they stay encrypted at rest in our database — but we recommend you leave them out.</li>
                    <li>We do <strong>not</strong> use ad-network tracking, cross-site advertising pixels, or remarketing tags. The analytics tools we <em>do</em> use are listed below.</li>
                </ul>
            </Section>

            <Section title="Analytics — what we use and why">
                <p>
                    To know whether Memory Lane is actually helping caregivers — and which parts to invest more in — we use a small set of privacy-respecting analytics tools. None of them are advertising trackers, and none of them are allowed to sell or share your data.
                </p>
                <ul>
                    <li>
                        <strong>Google Analytics 4 (GA4).</strong> Aggregated traffic and engagement
                        (which pages get visited, how long caregivers stay, which CTAs convert). We
                        run GA4 with IP anonymization enabled and we do <em>not</em> send any
                        identifying information about you to Google. We also fire a small set of
                        named events (e.g. <em>Account Created</em>, <em>Search Memory Lane</em>,
                        <em>Roadmap Completed</em>, <em>Care Navigator Session Booked</em>) so we
                        can measure how well the product is serving caregivers — never <em>what</em>
                        you typed.
                    </li>
                    <li>
                        <strong>PostHog (product analytics + occasional session replay).</strong>
                        Helps us understand where caregivers get stuck (e.g. &quot;people drop off on
                        step 3 of intake&quot;). When session replay is enabled it is masked by default
                        — input fields are redacted so what you type is <em>not</em> captured. We
                        use this to fix bugs and rough edges, not to spy.
                    </li>
                    <li>
                        <strong>Microsoft Clarity (heatmaps and behavioral analytics).</strong>{" "}
                        Shows us aggregated heatmaps and click patterns. Like PostHog, sensitive
                        form fields are masked. Clarity is a first-party tool that does not feed
                        advertising networks. Clarity only loads after you opt in via the cookie
                        consent banner (or never, if you decline or have Global Privacy Control on).
                    </li>
                    <li>
                        <strong>Our own backend telemetry.</strong> When a caregiver runs a search
                        that returns no results, we log just the search phrase (no account, no IP)
                        so we can write the article you couldn&apos;t find. We also log anonymous article
                        views (slug only) so we know which guides are most useful.
                    </li>
                </ul>
                <p>
                    You can change your mind at any time. The cookie consent banner you saw on your
                    first visit can be re-opened — until then, Google Analytics 4 cookies stay denied
                    via Google Consent Mode v2, and Microsoft Clarity does not load at all. You can
                    also block any of these at the browser level using any standard tracker blocker
                    (uBlock Origin, Privacy Badger, your browser&apos;s built-in tracking protection).
                    Memory Lane will continue to work normally either way. If your browser sends a
                    Global Privacy Control signal, we treat that as a decision to deny — no banner
                    shown, no analytics loaded.
                </p>
            </Section>

            <Section title="Cookies and similar technology">
                <p>
                    Memory Lane uses a small number of cookies and similar identifiers, grouped into
                    three categories:
                </p>
                <ul>
                    <li><strong>Strictly necessary</strong> — the login token and your session ID. Without these, you can&apos;t stay signed in or have a persistent place where your caregiving notes live.</li>
                    <li><strong>Analytics</strong> — GA4&apos;s <code>_ga</code> identifier, PostHog&apos;s <code>ph_</code> client ID, and Microsoft Clarity&apos;s <code>_clck</code>/<code>_clsk</code>. These are pseudonymous; they tell us how the product is used, not who you are. GA4 and Clarity only set their cookies <em>after</em> you accept the cookie banner.</li>
                    <li><strong>Preferences</strong> — your selected country and language, the &quot;show me the tour&quot; flag, and a few similar flags so the app remembers small choices you&apos;ve already made.</li>
                </ul>
                <p>
                    For the full list — including approximate lifespans — see our <Link to="/cookies" className="text-teal-dark underline">Cookie Policy</Link>.
                </p>
            </Section>

            <Section title="Why we collect what we collect">
                <ul>
                    <li><strong>To personalize Memory Lane.</strong> Your intake is what gives Lane (and your roadmap) context. Without it, you&apos;d get generic advice.</li>
                    <li><strong>To make Lane remember what worked.</strong> Behavior logs with &quot;what helped&quot; notes feed Lane&apos;s memory so it can bring back your wins next time.</li>
                    <li><strong>To improve the product.</strong> We look at aggregated, anonymized usage patterns (e.g., &quot;X% of caregivers in middle stage skip the appointment-prep tool&quot;) to decide what to build next. We do not look at individual users&apos; data unless you explicitly write to us and ask us to.</li>
                    <li><strong>To keep you safe.</strong> Login IPs and user agents help us detect suspicious access.</li>
                </ul>
            </Section>

            <Section title="The AI behind Lane">
                <p>
                    Lane is powered by large language models from OpenAI, Anthropic, and Google
                    (via our Emergent integrations key — a single managed gateway, not direct API
                    keys per provider). What each provider sees:
                </p>
                <ul>
                    <li>The text you typed into the chat.</li>
                    <li>A small context block built from your intake at the moment of the chat (e.g. &quot;caregiver of a parent, middle stage, top concern: wandering&quot;).</li>
                    <li>Optionally, prior Lane chat memory for continuity — only if you&apos;ve let it build.</li>
                </ul>
                <p>
                    All three providers are contractually prohibited from training their models on
                    Memory Lane traffic. Inputs to Lane are not shared with any other third party.
                </p>
                <p>
                    Lane is a guide, not a doctor. It can be wrong, biased, or out of date — always
                    verify clinical, legal, or financial guidance with a qualified human professional.
                </p>
            </Section>

            <Section title="Payments, scheduling, and email">
                <ul>
                    <li>
                        <strong>Stripe</strong> handles all Care Navigator session billing. Memory
                        Lane never sees or stores your card details — Stripe gives us back a token
                        and a payment status, nothing more.
                    </li>
                    <li>
                        <strong>Calendly</strong> is used to schedule Care Navigator and partner
                        intro calls. When you book, Calendly receives your name, email, and the slot
                        you picked.
                    </li>
                    <li>
                        <strong>Resend</strong> sends transactional email (welcome notes, share-link
                        notifications, magic links). Resend sees only the recipient email and the
                        message body. We do not run marketing campaigns from Resend; everything we
                        send is in response to something you did.
                    </li>
                </ul>
            </Section>

            <Section title="Who can see your data">
                <ul>
                    <li><strong>You.</strong> Always.</li>
                    <li><strong>A very small founding team.</strong> Only when necessary for operations, debugging, or support — and only on the encrypted database, never copied out to other systems.</li>
                    <li><strong>Our infrastructure partners.</strong> The data lives on cloud infrastructure (MongoDB Atlas for storage, our hosting provider for the app). They process data on our behalf under their own enterprise security agreements but do not have any independent rights to your content.</li>
                    <li><strong>Specific service providers</strong> for the narrow purposes above — AI providers, Stripe, Calendly, Resend, GA4, PostHog, and (when enabled) Microsoft Clarity. The <Link to="/security" className="text-teal-dark underline">Security page</Link> lists every sub-processor and exactly what they receive.</li>
                </ul>
                <p>
                    We do <strong>not</strong> share your data with advertisers, data brokers, employers, insurers, or family members. If a court ever issued a lawful subpoena, we would notify you (where legally permitted) before responding.
                </p>
            </Section>

            <Section title="How long we keep your data">
                <p>
                    For as long as your account is active. If you delete your account, we delete everything within 30 days, except where we&apos;re required to retain certain records for fraud or legal compliance (e.g., audit logs for security investigations — these never include your caregiving content). Analytics data in GA4/PostHog/Clarity is retained according to those providers&apos; default retention windows (typically 14 months for GA4).
                </p>
            </Section>

            <Section title="Your rights">
                <p>You can, at any time:</p>
                <ul>
                    <li>Ask us to <strong>show</strong> you everything we have about you.</li>
                    <li>Ask us to <strong>export</strong> it in a portable format (JSON or PDF — your choice).</li>
                    <li>Ask us to <strong>correct</strong> anything that is wrong.</li>
                    <li>Ask us to <strong>delete</strong> your account and all associated data.</li>
                    <li>Ask us to <strong>stop</strong> using your data for product improvement (we&apos;ll exclude your account from analytics queries).</li>
                </ul>
                <p>
                    Email <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>. We respond within 30 days. If you&apos;re in California, the EU/EEA, or the UK, you have additional statutory rights — we honor them globally even where not required.
                </p>
            </Section>

            <Section title="Children">
                <p>
                    Memory Lane is for caregivers age 18+. We don&apos;t knowingly collect data from children. If you believe a child has signed up, write us and we&apos;ll delete the account.
                </p>
            </Section>

            <Section title="Security">
                <p>
                    Passwords are hashed with bcrypt. Data in transit is TLS-encrypted. Data at rest in MongoDB is encrypted at the storage layer. Only a small team has database access, and access is logged.
                </p>
                <p>
                    No system is perfectly secure. If we ever experience a breach affecting your data, we will email you within 72 hours of confirming it, tell you what happened, and explain what we&apos;re doing about it.
                </p>
            </Section>

            <Section title="Changes to this policy">
                <p>
                    If we materially change this policy, we&apos;ll show a notice in the app and email approved users. Continued use after the change means you accept the updated policy.
                </p>
            </Section>

            <Section title="Contact">
                <p>
                    <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>
                </p>
            </Section>

            <p className="text-sm text-[#6E7A74] italic mt-12">
                We write our privacy policy in the language we use to talk to caregivers. It is not a substitute for legal advice — but it is the truth about what we do with your data.
            </p>
        </article>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-8">
            <h2 className="text-2xl font-heading font-semibold tracking-tight mb-3">{title}</h2>
            <div className="space-y-3 text-base">{children}</div>
        </section>
    );
}
